import { useState, useEffect, useRef, useCallback } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  Menu,
  Search,
  Users,
  Wine,
  Package,
  MapPin,
  Book,
  FileText,
  Beaker,
  ClipboardList,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Building2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useClients, useCreateClient, useUpdateClient, useDeleteClient, type Client, type CreateClientInput } from "@/hooks/useClients";
import { useVarieties, useCreateVariety, useUpdateVariety, useDeleteVariety, type Variety } from "@/hooks/useVarieties";
import { useStockTypes, useCreateStockType, useUpdateStockType, useDeleteStockType, type StockType, type StockTypeCategory } from "@/hooks/useStockTypes";
import { useSubAvas, useCreateSubAva, useUpdateSubAva, useDeleteSubAva, type SubAva } from "@/hooks/useSubAvas";
import { useWoActions, useCreateWoAction, useUpdateWoAction, useDeleteWoAction, type WoAction } from "@/hooks/useWoActions";
import { useFacilities, type Facility } from "@/hooks/useFacilities";
import { useFacilityZones, useCreateFacilityZone, useUpdateFacilityZone, useDeleteFacilityZone, type FacilityZone } from "@/hooks/useFacilityZones";

// Section definitions for the Glossary TOC
const glossarySections = [
  { id: "naming-conventions", title: "Naming Conventions", icon: Book, category: "reference" },
  { id: "wo-actions", title: "WO Actions", icon: ClipboardList, category: "reference" },
  { id: "client-codes", title: "Client Codes", icon: Users, category: "codes" },
  { id: "variety-codes", title: "Variety Codes", icon: Wine, category: "codes" },
  { id: "stock-types", title: "Stock Type Codes", icon: Package, category: "codes" },
  { id: "sub-avas", title: "Sub-AVAs", icon: MapPin, category: "regions" },
  { id: "locations", title: "Locations", icon: Building2, category: "facilities" },
];

const glossaryCategories = [
  { id: "reference", title: "Reference" },
  { id: "codes", title: "Codes & Identifiers" },
  { id: "regions", title: "Regions" },
  { id: "facilities", title: "Facilities" },
];

export function GlossaryTab() {
  const [activeSection, setActiveSection] = useState("naming-conventions");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(glossaryCategories.map((c) => c.id))
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Search states
  const [clientSearch, setClientSearch] = useState("");
  const [varietySearch, setVarietySearch] = useState("");
  const [stockTypeSearch, setStockTypeSearch] = useState("");
  const [avaSearch, setAvaSearch] = useState("");
  const [woActionSearch, setWoActionSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationFacilityFilter, setLocationFacilityFilter] = useState<string | null>(null);

  // Data hooks
  const { data: clients = [], isLoading: clientsLoading } = useClients();
  const { data: varieties = [], isLoading: varietiesLoading } = useVarieties(false);
  const { data: stockTypes = [], isLoading: stockTypesLoading } = useStockTypes(false);
  const { data: subAvas = [], isLoading: subAvasLoading } = useSubAvas(false);
  const { data: woActions = [], isLoading: woActionsLoading } = useWoActions(false);
  const { data: facilities = [], isLoading: facilitiesLoading } = useFacilities();
  const { data: facilityZones = [], isLoading: zonesLoading } = useFacilityZones();

  // Mutation hooks
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();
  const createVariety = useCreateVariety();
  const updateVariety = useUpdateVariety();
  const deleteVariety = useDeleteVariety();
  const createStockType = useCreateStockType();
  const updateStockType = useUpdateStockType();
  const deleteStockType = useDeleteStockType();
  const createSubAva = useCreateSubAva();
  const updateSubAva = useUpdateSubAva();
  const deleteSubAva = useDeleteSubAva();
  const createWoAction = useCreateWoAction();
  const updateWoAction = useUpdateWoAction();
  const deleteWoAction = useDeleteWoAction();
  const createFacilityZone = useCreateFacilityZone();
  const updateFacilityZone = useUpdateFacilityZone();
  const deleteFacilityZone = useDeleteFacilityZone();

  // Modal states
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [editingVariety, setEditingVariety] = useState<Variety | null>(null);
  const [isAddingVariety, setIsAddingVariety] = useState(false);
  const [editingStockType, setEditingStockType] = useState<StockType | null>(null);
  const [isAddingStockType, setIsAddingStockType] = useState(false);
  const [editingSubAva, setEditingSubAva] = useState<SubAva | null>(null);
  const [isAddingSubAva, setIsAddingSubAva] = useState(false);
  const [editingWoAction, setEditingWoAction] = useState<WoAction | null>(null);
  const [isAddingWoAction, setIsAddingWoAction] = useState(false);
  const [editingLocation, setEditingLocation] = useState<FacilityZone | null>(null);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string; name: string } | null>(null);

  // Form states
  const [formData, setFormData] = useState<Record<string, string>>({});

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const buffer = 100;
      let currentSection = activeSection;
      let minDistance = Infinity;

      sectionRefs.current.forEach((element, sectionId) => {
        const rect = element.getBoundingClientRect();
        const containerRect = contentRef.current!.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;

        if (relativeTop <= buffer && relativeTop > -rect.height / 2) {
          const distance = Math.abs(relativeTop);
          if (distance < minDistance) {
            minDistance = distance;
            currentSection = sectionId;
          }
        }
      });

      if (currentSection !== activeSection) {
        setActiveSection(currentSection);
      }
    };

    const container = contentRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [activeSection]);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = sectionRefs.current.get(sectionId);
    if (element && contentRef.current) {
      const containerRect = contentRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop =
        contentRef.current.scrollTop + elementRect.top - containerRect.top - 20;

      contentRef.current.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }
  }, []);

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    scrollToSection(sectionId);
    setMobileNavOpen(false);
  };

  const registerSectionRef = useCallback(
    (sectionId: string, element: HTMLElement | null) => {
      if (element) {
        sectionRefs.current.set(sectionId, element);
      } else {
        sectionRefs.current.delete(sectionId);
      }
    },
    []
  );

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Filter functions
  const filteredClients = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      (c.code?.toLowerCase() || "").includes(clientSearch.toLowerCase())
  );

  const filteredVarieties = varieties.filter(
    (v) =>
      v.name.toLowerCase().includes(varietySearch.toLowerCase()) ||
      v.code.toLowerCase().includes(varietySearch.toLowerCase())
  );

  const filteredStockTypes = stockTypes.filter(
    (s) =>
      s.name.toLowerCase().includes(stockTypeSearch.toLowerCase()) ||
      s.code.toLowerCase().includes(stockTypeSearch.toLowerCase())
  );

  const filteredSubAvas = subAvas.filter((a) =>
    a.name.toLowerCase().includes(avaSearch.toLowerCase())
  );

  const filteredWoActions = woActions.filter((a) =>
    a.name.toLowerCase().includes(woActionSearch.toLowerCase()) ||
    (a.note?.toLowerCase() || "").includes(woActionSearch.toLowerCase()) ||
    (a.action_type?.toLowerCase() || "").includes(woActionSearch.toLowerCase()) ||
    (a.action_subtype?.toLowerCase() || "").includes(woActionSearch.toLowerCase())
  );

  const filteredLocations = facilityZones.filter((z) =>
    (z.name.toLowerCase().includes(locationSearch.toLowerCase()) ||
    (z.description?.toLowerCase() || "").includes(locationSearch.toLowerCase())) &&
    (!locationFacilityFilter || z.facility_id === locationFacilityFilter)
  );

  // Group zones by facility for display
  const facilityMap = new Map(facilities.map((f) => [f.id, f]));
  const zonesByFacility = filteredLocations.reduce<Record<string, { facility: Facility; zones: FacilityZone[] }>>((acc, zone) => {
    const facility = facilityMap.get(zone.facility_id);
    if (!facility) return acc;
    if (!acc[zone.facility_id]) {
      acc[zone.facility_id] = { facility, zones: [] };
    }
    acc[zone.facility_id].zones.push(zone);
    return acc;
  }, {});

  // CRUD handlers
  const handleSaveClient = async () => {
    if (isAddingClient) {
      await createClient.mutateAsync({
        name: formData.name,
        first_name: formData.first_name || undefined,
        last_name: formData.last_name || undefined,
        code: formData.code || "",
      });
      setIsAddingClient(false);
    } else if (editingClient) {
      await updateClient.mutateAsync({
        id: editingClient.id,
        input: {
          name: formData.name,
          code: formData.code || undefined,
        },
      });
      setEditingClient(null);
    }
    setFormData({});
  };

  const handleSaveVariety = async () => {
    if (isAddingVariety) {
      await createVariety.mutateAsync({
        name: formData.name,
        code: formData.code,
        color: formData.color || undefined,
      });
      setIsAddingVariety(false);
    } else if (editingVariety) {
      await updateVariety.mutateAsync({
        id: editingVariety.id,
        input: { name: formData.name, code: formData.code, color: formData.color || null },
      });
      setEditingVariety(null);
    }
    setFormData({});
  };

  const handleSaveStockType = async () => {
    if (isAddingStockType) {
      await createStockType.mutateAsync({
        name: formData.name,
        code: formData.code,
        category: formData.category as StockTypeCategory,
      });
      setIsAddingStockType(false);
    } else if (editingStockType) {
      await updateStockType.mutateAsync({
        id: editingStockType.id,
        input: {
          name: formData.name,
          code: formData.code,
          category: formData.category as StockTypeCategory,
        },
      });
      setEditingStockType(null);
    }
    setFormData({});
  };

  const handleSaveSubAva = async () => {
    if (isAddingSubAva) {
      await createSubAva.mutateAsync({ name: formData.name });
      setIsAddingSubAva(false);
    } else if (editingSubAva) {
      await updateSubAva.mutateAsync({
        id: editingSubAva.id,
        input: { name: formData.name },
      });
      setEditingSubAva(null);
    }
    setFormData({});
  };

  const handleSaveWoAction = async () => {
    if (isAddingWoAction) {
      await createWoAction.mutateAsync({
        name: formData.name,
        note: formData.note || undefined,
      });
      setIsAddingWoAction(false);
    } else if (editingWoAction) {
      await updateWoAction.mutateAsync({
        id: editingWoAction.id,
        input: { name: formData.name, note: formData.note || null },
      });
      setEditingWoAction(null);
    }
    setFormData({});
  };

  const handleSaveLocation = async () => {
    if (isAddingLocation) {
      await createFacilityZone.mutateAsync({
        facility_id: formData.facility_id,
        name: formData.name,
        description: formData.description || undefined,
      });
      setIsAddingLocation(false);
    } else if (editingLocation) {
      await updateFacilityZone.mutateAsync({
        id: editingLocation.id,
        input: {
          name: formData.name,
          description: formData.description || null,
          facility_id: formData.facility_id,
        },
      });
      setEditingLocation(null);
    }
    setFormData({});
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    switch (deleteConfirm.type) {
      case "client":
        await deleteClient.mutateAsync(deleteConfirm.id);
        break;
      case "variety":
        await deleteVariety.mutateAsync(deleteConfirm.id);
        break;
      case "stockType":
        await deleteStockType.mutateAsync(deleteConfirm.id);
        break;
      case "subAva":
        await deleteSubAva.mutateAsync(deleteConfirm.id);
        break;
      case "woAction":
        await deleteWoAction.mutateAsync(deleteConfirm.id);
        break;
      case "location":
        await deleteFacilityZone.mutateAsync(deleteConfirm.id);
        break;
    }
    setDeleteConfirm(null);
  };

  // Table of Contents Component
  const TableOfContents = () => (
    <nav className="space-y-1">
      {glossaryCategories.map((category) => {
        const sections = glossarySections.filter((s) => s.category === category.id);
        const isExpanded = expandedCategories.has(category.id);
        const hasActiveSection = sections.some((s) => s.id === activeSection);

        return (
          <div key={category.id} className="space-y-1">
            <button
              onClick={() => toggleCategory(category.id)}
              className={cn(
                "flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium rounded-md transition-colors",
                "hover:bg-muted text-left",
                hasActiveSection && "text-primary"
              )}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0" />
              )}
              {category.title}
            </button>

            {isExpanded && (
              <div className="ml-4 space-y-0.5 border-l pl-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = section.id === activeSection;

                  return (
                    <button
                      key={section.id}
                      onClick={() => handleSectionClick(section.id)}
                      className={cn(
                        "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md transition-colors text-left",
                        "hover:bg-muted",
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{section.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <TabsContent value="glossary" className="space-y-4">
      {/* Mobile Navigation */}
      <div className="lg:hidden flex items-center gap-2">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72">
            <div className="mt-4">
              <h3 className="font-semibold mb-4">Table of Contents</h3>
              <TableOfContents />
            </div>
          </SheetContent>
        </Sheet>
        <span className="text-sm text-muted-foreground">
          {glossarySections.find((s) => s.id === activeSection)?.title}
        </span>
      </div>

      {/* Desktop Layout */}
      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-0 space-y-4">
            <Card>
              <CardContent className="p-4">
                <TableOfContents />
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* Main Content */}
        <main
          ref={contentRef}
          className="flex-1 min-w-0 max-h-[calc(100vh-200px)] overflow-y-auto pr-2 space-y-6"
        >
          {/* Naming Conventions */}
          <Card
            ref={(el) => registerSectionRef("naming-conventions", el)}
            id="naming-conventions"
            className={cn(
              "transition-shadow",
              activeSection === "naming-conventions" && "ring-2 ring-primary/20"
            )}
          >
            <CardHeader>
              <div className="flex items-center gap-2">
                <Book className="h-5 w-5" />
                <CardTitle>Naming Conventions</CardTitle>
              </div>
              <CardDescription>
                Standard protocols for Vintrace & Dropbox file naming
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 text-sm">
              {/* Wine Lot ID */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Wine className="h-4 w-4" />
                  <h3 className="text-base font-semibold">Wine Lot ID Coding</h3>
                  <Badge variant="secondary">Vintrace</Badge>
                </div>
                <div className="bg-muted p-4 rounded-lg font-mono text-center">
                  [ClientCode][Vintage][Variety][Unique Identifier]
                </div>
                <div className="bg-rose-50 dark:bg-rose-950/20 border-l-4 border-rose-600 p-4 rounded-r-lg">
                  <p className="text-sm text-muted-foreground mb-2">Example:</p>
                  <p className="font-mono text-lg font-semibold">PL25PNGV1A</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="bg-background px-2 py-1 rounded border">PL = Pisoni</span>
                    <span className="bg-background px-2 py-1 rounded border">25 = 2025</span>
                    <span className="bg-background px-2 py-1 rounded border">PN = Pinot Noir</span>
                    <span className="bg-background px-2 py-1 rounded border">GV1A = Block/Vineyard ID</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Stock Item Coding */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  <h3 className="text-base font-semibold">Stock Item Coding</h3>
                  <Badge variant="secondary">Vintrace</Badge>
                </div>
                <div className="bg-muted p-4 rounded-lg font-mono text-center">
                  [ClientCode] - [StockType] - [Unique Identifier]
                </div>
                <div className="bg-rose-50 dark:bg-rose-950/20 border-l-4 border-rose-600 p-4 rounded-r-lg">
                  <p className="text-sm text-muted-foreground mb-2">Example:</p>
                  <p className="font-mono text-lg font-semibold">PL - ML - ALPHA</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="bg-background px-2 py-1 rounded border">PL = Pisoni</span>
                    <span className="bg-background px-2 py-1 rounded border">ML = Malolactic Bacteria</span>
                    <span className="bg-background px-2 py-1 rounded border">ALPHA = Product Name</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Dropbox File Naming */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <h3 className="text-base font-semibold">Dropbox File Naming</h3>
                  <Badge variant="secondary">Dropbox</Badge>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <h4 className="font-medium">Bills of Lading (BOLs)</h4>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs">
                      [YYYY.MM.DD] [ClientCode] [Action]
                    </div>
                    <p className="text-xs text-muted-foreground">
                      e.g., <code className="bg-muted px-1 rounded">2025.03.20 PL bottled wine out</code>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">Brix & Temperature</h4>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs">
                      [YYYY.MM.DD] [ClientCode] BT [whites/reds]
                    </div>
                    <p className="text-xs text-muted-foreground">
                      e.g., <code className="bg-muted px-1 rounded">2025.10.19 PL BT reds</code>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">Extractions</h4>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs">
                      [YYYY.MM.DD] [ClientCode] Extractions
                    </div>
                    <p className="text-xs text-muted-foreground">
                      e.g., <code className="bg-muted px-1 rounded">2025.10.19 PL Extractions</code>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">Weigh Tags</h4>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs">
                      [YYYY.MM.DD] [ClientCode] [Variety][ID] weigh tag
                    </div>
                    <p className="text-xs text-muted-foreground">
                      e.g., <code className="bg-muted px-1 rounded">2025.10.19 PL PNGV3 weigh tag</code>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">Work Orders</h4>
                    <div className="bg-muted p-3 rounded-lg font-mono text-xs">
                      [YYYY.MM.DD] [ClientCode/LotID] [Action]
                    </div>
                    <p className="text-xs text-muted-foreground">
                      e.g., <code className="bg-muted px-1 rounded">2025.10.12 PL25PNSV6A ML inoc</code>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* WO Actions */}
          <Card
            ref={(el) => registerSectionRef("wo-actions", el)}
            id="wo-actions"
            className={cn(
              "transition-shadow",
              activeSection === "wo-actions" && "ring-2 ring-primary/20"
            )}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  <CardTitle>WO Actions</CardTitle>
                  <Badge variant="outline">{woActions.filter(a => a.is_active).length} actions</Badge>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsAddingWoAction(true);
                    setFormData({ name: "", note: "" });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <CardDescription>Standard work order action types used across operations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search actions..."
                  value={woActionSearch}
                  onChange={(e) => setWoActionSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-80 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-semibold">Action</th>
                      <th className="text-left p-3 font-semibold">Type</th>
                      <th className="text-left p-3 font-semibold">Subtype</th>
                      <th className="text-left p-3 font-semibold">Empties Tank</th>
                      <th className="text-left p-3 font-semibold">Note</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-right p-3 font-semibold w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {woActionsLoading ? (
                      <tr>
                        <td colSpan={7} className="p-3 text-center text-muted-foreground">
                          Loading...
                        </td>
                      </tr>
                    ) : filteredWoActions.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-3 text-center text-muted-foreground">
                          No actions found
                        </td>
                      </tr>
                    ) : (
                      filteredWoActions.map((action) => (
                        <tr key={action.id} className={cn("border-t hover:bg-muted/50", !action.is_active && "opacity-50")}>
                          <td className="p-3 font-medium">{action.name}</td>
                          <td className="p-3 text-muted-foreground">{action.action_type || "-"}</td>
                          <td className="p-3 text-muted-foreground">{action.action_subtype || "-"}</td>
                          <td className="p-3">
                            {action.tank_emptying ? (
                              <Badge variant="destructive" className="text-xs">Yes</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3 text-muted-foreground">{action.note || "-"}</td>
                          <td className="p-3">
                            <Badge variant={action.is_active ? "default" : "secondary"}>
                              {action.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingWoAction(action);
                                setFormData({ name: action.name, note: action.note || "" });
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {action.is_active && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => setDeleteConfirm({ type: "woAction", id: action.id, name: action.name })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Client Codes */}
          <Card
            ref={(el) => registerSectionRef("client-codes", el)}
            id="client-codes"
            className={cn(
              "transition-shadow",
              activeSection === "client-codes" && "ring-2 ring-primary/20"
            )}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  <CardTitle>Client Codes</CardTitle>
                  <Badge variant="outline">{clients.length} clients</Badge>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsAddingClient(true);
                    setFormData({ name: "", code: "", first_name: "", last_name: "" });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <CardDescription>Two-letter codes for client identification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-semibold">Client</th>
                      <th className="text-left p-3 font-semibold">Code</th>
                      <th className="text-right p-3 font-semibold w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientsLoading ? (
                      <tr>
                        <td colSpan={3} className="p-3 text-center text-muted-foreground">
                          Loading...
                        </td>
                      </tr>
                    ) : filteredClients.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-3 text-center text-muted-foreground">
                          No clients found
                        </td>
                      </tr>
                    ) : (
                      filteredClients.map((client) => (
                        <tr key={client.id} className="border-t hover:bg-muted/50">
                          <td className="p-3">{client.name}</td>
                          <td className="p-3 font-mono font-semibold">{client.code || "-"}</td>
                          <td className="p-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingClient(client);
                                setFormData({ name: client.name, code: client.code || "" });
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => setDeleteConfirm({ type: "client", id: client.id, name: client.name })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Variety Codes */}
          <Card
            ref={(el) => registerSectionRef("variety-codes", el)}
            id="variety-codes"
            className={cn(
              "transition-shadow",
              activeSection === "variety-codes" && "ring-2 ring-primary/20"
            )}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wine className="h-5 w-5" />
                  <CardTitle>Variety Codes</CardTitle>
                  <Badge variant="outline">{varieties.filter(v => v.is_active).length} varieties</Badge>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsAddingVariety(true);
                    setFormData({ name: "", code: "" });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <CardDescription>Two-letter codes for grape varieties</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search varieties..."
                  value={varietySearch}
                  onChange={(e) => setVarietySearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-64 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-semibold">Variety</th>
                      <th className="text-left p-3 font-semibold">Code</th>
                      <th className="text-left p-3 font-semibold">Color</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-right p-3 font-semibold w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {varietiesLoading ? (
                      <tr>
                        <td colSpan={5} className="p-3 text-center text-muted-foreground">
                          Loading...
                        </td>
                      </tr>
                    ) : filteredVarieties.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-3 text-center text-muted-foreground">
                          No varieties found
                        </td>
                      </tr>
                    ) : (
                      filteredVarieties.map((variety) => (
                        <tr key={variety.id} className={cn("border-t hover:bg-muted/50", !variety.is_active && "opacity-50")}>
                          <td className="p-3">{variety.name}</td>
                          <td className="p-3 font-mono font-semibold">{variety.code}</td>
                          <td className="p-3">
                            {variety.color ? (
                              <div className="flex items-center gap-2">
                                <span
                                  className="inline-block h-4 w-4 rounded-full border border-border flex-shrink-0"
                                  style={{ backgroundColor: variety.color }}
                                />
                                <span className="text-xs text-muted-foreground font-mono">{variety.color}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge variant={variety.is_active ? "default" : "secondary"}>
                              {variety.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingVariety(variety);
                                setFormData({ name: variety.name, code: variety.code, color: variety.color || "" });
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {variety.is_active && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => setDeleteConfirm({ type: "variety", id: variety.id, name: variety.name })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Stock Type Codes */}
          <Card
            ref={(el) => registerSectionRef("stock-types", el)}
            id="stock-types"
            className={cn(
              "transition-shadow",
              activeSection === "stock-types" && "ring-2 ring-primary/20"
            )}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  <CardTitle>Stock Type Codes</CardTitle>
                  <Badge variant="outline">{stockTypes.filter(s => s.is_active).length} types</Badge>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsAddingStockType(true);
                    setFormData({ name: "", code: "", category: "additive" });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <CardDescription>Codes for additives and packaging materials</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search stock types..."
                  value={stockTypeSearch}
                  onChange={(e) => setStockTypeSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {/* Additives */}
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Beaker className="h-4 w-4" />
                    Additives
                  </h4>
                  <div className="space-y-2">
                    {stockTypesLoading ? (
                      <p className="text-sm text-muted-foreground">Loading...</p>
                    ) : (
                      filteredStockTypes
                        .filter((s) => s.category === "additive")
                        .map((item) => (
                          <div
                            key={item.id}
                            className={cn(
                              "flex justify-between items-center py-2 px-3 bg-muted/50 rounded group",
                              !item.is_active && "opacity-50"
                            )}
                          >
                            <span className="text-sm">{item.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-sm bg-background px-2 py-1 rounded border">
                                {item.code}
                              </span>
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => {
                                    setEditingStockType(item);
                                    setFormData({ name: item.name, code: item.code, category: item.category });
                                  }}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                {item.is_active && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-destructive"
                                    onClick={() => setDeleteConfirm({ type: "stockType", id: item.id, name: item.name })}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>

                {/* Packaging */}
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Packaging
                  </h4>
                  <div className="space-y-2">
                    {stockTypesLoading ? (
                      <p className="text-sm text-muted-foreground">Loading...</p>
                    ) : (
                      filteredStockTypes
                        .filter((s) => s.category === "packaging")
                        .map((item) => (
                          <div
                            key={item.id}
                            className={cn(
                              "flex justify-between items-center py-2 px-3 bg-muted/50 rounded group",
                              !item.is_active && "opacity-50"
                            )}
                          >
                            <span className="text-sm">{item.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-sm bg-background px-2 py-1 rounded border">
                                {item.code}
                              </span>
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => {
                                    setEditingStockType(item);
                                    setFormData({ name: item.name, code: item.code, category: item.category });
                                  }}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                {item.is_active && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-destructive"
                                    onClick={() => setDeleteConfirm({ type: "stockType", id: item.id, name: item.name })}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sub-AVAs */}
          <Card
            ref={(el) => registerSectionRef("sub-avas", el)}
            id="sub-avas"
            className={cn(
              "transition-shadow",
              activeSection === "sub-avas" && "ring-2 ring-primary/20"
            )}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  <CardTitle>Sub-AVAs</CardTitle>
                  <Badge variant="outline">{subAvas.filter(a => a.is_active).length} regions</Badge>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsAddingSubAva(true);
                    setFormData({ name: "" });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <CardDescription>American Viticultural Areas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search AVAs..."
                  value={avaSearch}
                  onChange={(e) => setAvaSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {subAvasLoading ? (
                    <span className="col-span-full text-center text-muted-foreground py-4">
                      Loading...
                    </span>
                  ) : filteredSubAvas.length === 0 ? (
                    <span className="col-span-full text-center text-muted-foreground py-4">
                      No AVAs found
                    </span>
                  ) : (
                    filteredSubAvas.map((ava) => (
                      <div
                        key={ava.id}
                        className={cn(
                          "bg-muted/50 text-sm px-3 py-2 rounded-lg hover:bg-muted transition-colors group flex justify-between items-center",
                          !ava.is_active && "opacity-50"
                        )}
                      >
                        <span className="truncate">{ava.name}</span>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-shrink-0 ml-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setEditingSubAva(ava);
                              setFormData({ name: ava.name });
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          {ava.is_active && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive"
                              onClick={() => setDeleteConfirm({ type: "subAva", id: ava.id, name: ava.name })}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Locations */}
          <Card
            ref={(el) => registerSectionRef("locations", el)}
            id="locations"
            className={cn(
              "transition-shadow",
              activeSection === "locations" && "ring-2 ring-primary/20"
            )}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  <CardTitle>Locations</CardTitle>
                  <Badge variant="outline">{facilityZones.length} areas</Badge>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsAddingLocation(true);
                    setFormData({ facility_id: facilities[0]?.id || "" });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <CardDescription>Facility areas and their usage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search locations..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant={locationFacilityFilter === null ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => setLocationFacilityFilter(null)}
                  >
                    All
                  </Button>
                  {facilities.filter(f => f.is_active && (f.code === 'GWH' || f.code === 'RP')).sort((a, b) => a.code.localeCompare(b.code)).map((f) => (
                    <Button
                      key={f.id}
                      variant={locationFacilityFilter === f.id ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 text-xs font-mono"
                      onClick={() => setLocationFacilityFilter(locationFacilityFilter === f.id ? null : f.id)}
                    >
                      {f.code}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="max-h-[32rem] overflow-y-auto space-y-4">
                {facilitiesLoading || zonesLoading ? (
                  <span className="text-center text-muted-foreground py-4 block">Loading...</span>
                ) : Object.keys(zonesByFacility).length === 0 ? (
                  <span className="text-center text-muted-foreground py-4 block">No locations found</span>
                ) : (
                  Object.values(zonesByFacility)
                    .sort((a, b) => a.facility.code.localeCompare(b.facility.code))
                    .map(({ facility, zones }) => (
                      <div key={facility.id}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="font-mono text-xs">
                            {facility.code}
                          </Badge>
                          <span className="text-sm font-medium text-muted-foreground">
                            {facility.name}
                          </span>
                        </div>
                        <div className="space-y-1 ml-1">
                          {zones
                            .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
                            .map((zone) => (
                              <div
                                key={zone.id}
                                className="bg-muted/50 text-sm px-3 py-2 rounded-lg hover:bg-muted transition-colors group flex justify-between items-start gap-2"
                              >
                                <div className="min-w-0">
                                  <span className="font-medium">{zone.name}</span>
                                  {zone.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                      {zone.description}
                                    </p>
                                  )}
                                </div>
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                      setEditingLocation(zone);
                                      setFormData({
                                        name: zone.name,
                                        description: zone.description || "",
                                        facility_id: zone.facility_id,
                                      });
                                    }}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-destructive"
                                    onClick={() => setDeleteConfirm({ type: "location", id: zone.id, name: zone.name })}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                        </div>
                        <Separator className="mt-3" />
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Add/Edit Client Dialog */}
      <Dialog open={isAddingClient || !!editingClient} onOpenChange={() => { setIsAddingClient(false); setEditingClient(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isAddingClient ? "Add Client" : "Edit Client"}</DialogTitle>
            <DialogDescription>
              {isAddingClient ? "Add a new client with their code" : `Update ${editingClient?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client-name">Company Name</Label>
              <Input
                id="client-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Pisoni Vineyards"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-code">Code *</Label>
              <Input
                id="client-code"
                value={formData.code || ""}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., PL"
                maxLength={4}
              />
            </div>
            {isAddingClient && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client-first-name">Contact First Name</Label>
                    <Input
                      id="client-first-name"
                      value={formData.first_name || ""}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="e.g., John"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-last-name">Contact Last Name</Label>
                    <Input
                      id="client-last-name"
                      value={formData.last_name || ""}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="e.g., Smith"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddingClient(false); setEditingClient(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveClient}
              disabled={createClient.isPending || updateClient.isPending || !formData.name || !formData.code?.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Variety Dialog */}
      <Dialog open={isAddingVariety || !!editingVariety} onOpenChange={() => { setIsAddingVariety(false); setEditingVariety(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isAddingVariety ? "Add Variety" : "Edit Variety"}</DialogTitle>
            <DialogDescription>
              {isAddingVariety ? "Add a new grape variety" : `Update ${editingVariety?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="variety-name">Name</Label>
              <Input
                id="variety-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Pinot Noir"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="variety-code">Code</Label>
              <Input
                id="variety-code"
                value={formData.code || ""}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., PN"
                maxLength={4}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="variety-color">Color</Label>
              <div className="flex items-center gap-3">
                <input
                  id="variety-color"
                  type="color"
                  value={formData.color || "#6B4070"}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-9 w-12 rounded border border-input cursor-pointer bg-transparent p-0.5"
                />
                <Input
                  value={formData.color || ""}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="#6B4070"
                  className="flex-1 font-mono"
                  maxLength={7}
                />
                {formData.color && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={() => setFormData({ ...formData, color: "" })}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddingVariety(false); setEditingVariety(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveVariety}
              disabled={createVariety.isPending || updateVariety.isPending || !formData.name || !formData.code}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Stock Type Dialog */}
      <Dialog open={isAddingStockType || !!editingStockType} onOpenChange={() => { setIsAddingStockType(false); setEditingStockType(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isAddingStockType ? "Add Stock Type" : "Edit Stock Type"}</DialogTitle>
            <DialogDescription>
              {isAddingStockType ? "Add a new stock type" : `Update ${editingStockType?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="stock-name">Name</Label>
              <Input
                id="stock-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Malolactic Bacteria"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock-code">Code</Label>
              <Input
                id="stock-code"
                value={formData.code || ""}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., ML"
                maxLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock-category">Category</Label>
              <Select
                value={formData.category || "additive"}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="additive">Additive</SelectItem>
                  <SelectItem value="packaging">Packaging</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddingStockType(false); setEditingStockType(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveStockType}
              disabled={createStockType.isPending || updateStockType.isPending || !formData.name || !formData.code}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Sub-AVA Dialog */}
      <Dialog open={isAddingSubAva || !!editingSubAva} onOpenChange={() => { setIsAddingSubAva(false); setEditingSubAva(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isAddingSubAva ? "Add Sub-AVA" : "Edit Sub-AVA"}</DialogTitle>
            <DialogDescription>
              {isAddingSubAva ? "Add a new American Viticultural Area" : `Update ${editingSubAva?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ava-name">Name</Label>
              <Input
                id="ava-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Russian River Valley"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddingSubAva(false); setEditingSubAva(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveSubAva}
              disabled={createSubAva.isPending || updateSubAva.isPending || !formData.name}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit WO Action Dialog */}
      <Dialog open={isAddingWoAction || !!editingWoAction} onOpenChange={() => { setIsAddingWoAction(false); setEditingWoAction(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isAddingWoAction ? "Add WO Action" : "Edit WO Action"}</DialogTitle>
            <DialogDescription>
              {isAddingWoAction ? "Add a new work order action type" : `Update ${editingWoAction?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="woaction-name">Action Name</Label>
              <Input
                id="woaction-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Racking"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="woaction-note">Note (optional)</Label>
              <Input
                id="woaction-note"
                value={formData.note || ""}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="e.g., Refers to Yeast"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddingWoAction(false); setEditingWoAction(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveWoAction}
              disabled={createWoAction.isPending || updateWoAction.isPending || !formData.name}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Location Dialog */}
      <Dialog open={isAddingLocation || !!editingLocation} onOpenChange={() => { setIsAddingLocation(false); setEditingLocation(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isAddingLocation ? "Add Location" : "Edit Location"}</DialogTitle>
            <DialogDescription>
              {isAddingLocation ? "Add a new facility area" : `Update ${editingLocation?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="location-facility">Facility</Label>
              <Select
                value={formData.facility_id || ""}
                onValueChange={(value) => setFormData({ ...formData, facility_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select facility" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.filter(f => f.is_active).map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.code} — {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location-name">Area Name</Label>
              <Input
                id="location-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Main cellar"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location-description">Usage / Description</Label>
              <Input
                id="location-description"
                value={formData.description || ""}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Bottling and fermentation tanks, barrel filling"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddingLocation(false); setEditingLocation(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveLocation}
              disabled={createFacilityZone.isPending || updateFacilityZone.isPending || !formData.name || !formData.facility_id}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {deleteConfirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the item and it will no longer appear in dropdowns.
              This action can be undone by editing the item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabsContent>
  );
}
